import { templateResult } from '@/utils/templates'
import { deviceControlReadonly } from './device-control'
import { isLiveProjectPreview } from './shared-view'

const object = value => value && typeof value === 'object' && !Array.isArray(value)
const id = value => typeof value === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(value)
const nodeId = value => typeof value === 'string' && /^[A-Za-z0-9_-]{1,128}$/.test(value)
const digest = value => typeof value === 'string' && /^[a-f0-9]{64}$/.test(value)
const integer = value => Number.isSafeInteger(value) && value >= 0
const scalar = value => typeof value === 'boolean' || (typeof value === 'string' && value.length <= 4096) || (typeof value === 'number' && Number.isFinite(value))
const countKeys = ['pending', 'sending', 'succeeded', 'failed', 'unknown', 'skipped']
const taskStates = ['queued', 'running', 'succeeded', 'partial', 'failed', 'unknown']
const sameBinding = (value, scope) => ['projectId', 'pageId', 'componentId', 'groupId', 'productId', 'propertyId'].every(key => value[key] === scope[key])
const fail = message => { throw new Error(message) }

export const groupControlReadonly = host => deviceControlReadonly(host)

// 群控prepare先GET能力，必须在GET可能触发自动续期前拒绝过期时的旧点击，恢复后由用户重新发起。
export function groupControlStartReady (host) {
  if (groupControlReadonly(host)) return false
  if (!host.isSharedView) return true
  if (typeof host.sharedControlUsable === 'function') return host.sharedControlUsable() === true
  return host.shareBrowserVersion !== 1 && host.sharedControlReady !== false
}

export function groupControlScope (host, componentId, option = {}) {
  if (groupControlReadonly(host)) fail('编辑画布、模板及未授权分享不能执行群控')
  const scope = { projectId: host.isSharedView ? host.shareProjectId : host.projectRecord && host.projectRecord.id,
    pageId: host.activePageId, componentId: String(componentId), groupId: option.groupId, productId: option.productId,
    propertyId: option.propertyId, valueSource: option.valueSource === undefined ? 'fixed' : option.valueSource,
    targetValue: option.valueSource === 'runtime' ? null : option.targetValue }
  if (!['string', 'number'].includes(typeof componentId) || !id(scope.projectId) || !nodeId(scope.pageId) || !nodeId(scope.componentId) ||
      !id(scope.groupId) || !id(scope.productId) || typeof scope.propertyId !== 'string' || !/^[\p{L}\p{N}_.:-]{1,128}$/u.test(scope.propertyId) ||
      !['fixed', 'runtime'].includes(scope.valueSource) || (scope.valueSource === 'fixed' && !scalar(scope.targetValue))) fail('请先完整配置群组、控制属性和输入方式并保存')
  if (host.isSharedView) scope.shared = true
  if (isLiveProjectPreview(host)) {
    const version = host.obj && host.obj.previewControlVersionId
    if (!id(version)) fail('预览草稿版本未就绪，请保存后重新打开预览')
    scope.previewVersionId = version
  }
  return scope
}

export function groupControlTarget (scope) {
  return { pageId: scope.pageId, componentId: scope.componentId, ...(scope.previewVersionId ? { previewVersionId: scope.previewVersionId } : {}) }
}

export function groupControlInput (scope, value, capability) {
  if (scope.valueSource !== 'runtime') return groupControlTarget(scope)
  if (!scalar(value)) fail('请输入一个有效的群组属性目标值')
  if (!capability || capability.valueSource !== 'runtime' || !digest(capability.propertyHash)) fail('群组属性类型尚未核对，请重新读取能力后设置目标')
  return { ...groupControlTarget(scope), value, propertyHash: capability.propertyHash }
}

function version (value, scope) {
  if (scope.previewVersionId) {
    if (value.controlSource !== 'preview' || value.controlVersionId !== scope.previewVersionId || value.previewVersionId !== scope.previewVersionId || value.publishedVersionId !== '') fail('群控预览版本回执无效，请重新保存并打开预览')
  } else if (!id(value.controlVersionId) || value.publishedVersionId !== value.controlVersionId || value.controlSource !== (scope.shared ? 'share' : 'published') ||
      (value.previewVersionId !== undefined && value.previewVersionId !== null && value.previewVersionId !== '')) fail('群控发布版本回执无效，请重新读取')
  return value.controlVersionId
}

function result (response, scope) {
  const value = templateResult(response)
  if (!object(value) || !sameBinding(value, scope)) fail('群控回执与当前组件绑定不一致，请重新核对')
  version(value, scope)
  if (scope.valueSource === 'runtime' ? value.valueSource !== 'runtime' : value.valueSource !== undefined && value.valueSource !== 'fixed') fail('群控输入方式回执不一致，请保存并更新前后端后重新打开')
  return value
}

export function groupControlCapability (response, scope) {
  const value = result(response, scope)
  // runtime能力只提供输入约束，还没有目标值；平台可能省略null，固定目标及预检回执仍要求原值。
  if (typeof value.writable !== 'boolean' || typeof value.reason !== 'string' || !integer(value.maxMembers) || value.maxMembers < 1 || value.maxMembers > 100 ||
      !integer(value.groupRevision) || value.groupRevision < 1 || (scope.valueSource === 'runtime' ? (value.targetValue !== null && value.targetValue !== undefined) || !digest(value.propertyHash) : !scalar(value.targetValue) || !Object.is(value.targetValue, scope.targetValue)) ||
      !object(value.property) || value.property.id !== scope.propertyId || typeof value.property.writable !== 'boolean' || (value.writable && !value.property.writable)) fail('群控能力或目标值回执无效，请重新读取')
  return { ...value, valueSource: value.valueSource || 'fixed', targetValue: scope.valueSource === 'runtime' ? null : value.targetValue }
}

export function groupControlCheck (response, scope, capability, targetValue) {
  const value = result(response, scope)
  if (!capability || value.controlVersionId !== capability.controlVersionId || value.groupRevision !== capability.groupRevision || !digest(value.preflightHash) ||
      !integer(value.memberCount) || value.memberCount < 1 || value.memberCount > capability.maxMembers || !integer(value.readyCount) || value.readyCount < 1 ||
      !integer(value.skippedCount) || value.readyCount + value.skippedCount !== value.memberCount || value.maxMembers !== capability.maxMembers ||
      (scope.valueSource === 'runtime' && (!scalar(targetValue) || !Object.is(value.targetValue, targetValue) || value.propertyHash !== capability.propertyHash))) fail('群组成员、目标值或执行条件已变化，请重新预检')
  return value
}

export function groupControlTask (response, scope, requestId) {
  const value = result(response, scope)
  if (!digest(value.taskId) || value.requestId !== requestId || !taskStates.includes(value.state) || !integer(value.groupRevision) || value.groupRevision < 1 ||
      !integer(value.memberCount) || value.memberCount < 1 || value.memberCount > 100 || !object(value.counts) || countKeys.some(key => !integer(value.counts[key])) ||
      countKeys.reduce((total, key) => total + value.counts[key], 0) !== value.memberCount || !integer(value.createdTime) || !integer(value.deadline) || value.deadline <= value.createdTime ||
      (value.completedTime !== undefined && value.completedTime !== null && !integer(value.completedTime))) fail('群控任务结果未确认，请查询原请求，不要重复下发')
  const terminal = !['queued', 'running'].includes(value.state)
  if ((terminal && value.counts.pending + value.counts.sending !== 0) || (value.state === 'succeeded' && value.counts.succeeded !== value.memberCount) ||
      (value.state === 'partial' && (!value.counts.succeeded || value.counts.succeeded === value.memberCount)) ||
      (value.state === 'unknown' && (!value.counts.unknown || value.counts.succeeded)) || (value.state === 'failed' && (value.counts.succeeded || value.counts.unknown))) fail('群控任务统计不一致，请查询原任务核对')
  return value
}

export function groupControlDetails (response, task) {
  const value = templateResult(response)
  if (!object(value) || value.taskId !== task.taskId || value.requestId !== task.requestId || !integer(value.pageIndex) || !integer(value.pageSize) || value.pageSize < 1 || value.pageSize > 100 ||
      !integer(value.total) || value.total !== task.memberCount || !Array.isArray(value.data) || value.data.length > value.pageSize ||
      value.data.length !== Math.min(value.pageSize, Math.max(0, value.total - value.pageIndex * value.pageSize)) || (value.total && value.pageIndex * value.pageSize >= value.total)) fail('本次执行明细回执无效，请重新查询')
  const ids = new Set()
  value.data.forEach(row => {
    if (!object(row) || !id(row.deviceId) || ids.has(row.deviceId) || typeof row.name !== 'string' || row.name.length > 512 || !countKeys.includes(row.state) ||
        typeof row.message !== 'string' || row.message.length > 1024 || typeof row.confirmedResponse !== 'boolean' || (row.state === 'succeeded' && !row.confirmedResponse)) fail('本次执行明细无效，请重新查询')
    ids.add(row.deviceId)
  })
  return value
}

export const pendingGroupControl = task => !!task && ['queued', 'running'].includes(task.state)
export const groupControlStateText = task => !task ? '' : ({ queued: '任务已受理', running: '正在执行', succeeded: '全部设备已返回成功结果',
  partial: '部分设备已返回成功结果，请核对明细', failed: '本次任务未成功，请核对明细', unknown: '执行结果未知，请核实，勿重复下发' })[task.state] || '结果待核实'
